import { FC, useState } from 'react'
import {
  buildEmbedSnippet,
  EMBED_DEFAULT_BUTTON_COLOR,
  EMBED_DEFAULT_BUTTON_SIZE,
  EMBED_DEFAULT_LABEL,
  EMBED_DEFAULT_POSITIONING,
  EMBED_DEFAULT_TEXT_COLOR,
  type ButtonSizeOption,
  type PositioningOption,
  VALID_BUTTON_SIZES,
  VALID_POSITIONING,
} from '@shared/core/bookings'
import { Button, FormControl, Modal, TextField, Typography } from 'src/modules/core/components'
import classes from './add-to-website-modal.module.scss'

const STORAGE_KEY = 'bwm-embed-settings'

interface StoredEmbedSettings {
  linkText: string
  buttonColor: string
  textColor: string
  buttonSize: ButtonSizeOption
  positioning: PositioningOption
}

function getDefaultSettings(props: {
  initialLabel?: string
  initialButtonColor?: string
  initialTextColor?: string
  initialButtonSize?: ButtonSizeOption
  initialPositioning?: PositioningOption
}): StoredEmbedSettings {
  return {
    linkText: props.initialLabel ?? EMBED_DEFAULT_LABEL,
    buttonColor: props.initialButtonColor ?? EMBED_DEFAULT_BUTTON_COLOR,
    textColor: props.initialTextColor ?? EMBED_DEFAULT_TEXT_COLOR,
    buttonSize: props.initialButtonSize ?? EMBED_DEFAULT_BUTTON_SIZE,
    positioning: props.initialPositioning ?? EMBED_DEFAULT_POSITIONING,
  }
}

function loadSettingsFromStorage(props: AddToWebsiteModalProps): StoredEmbedSettings {
  const defaults = getDefaultSettings(props)
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as Partial<StoredEmbedSettings>
    return {
      linkText: typeof parsed.linkText === 'string' ? parsed.linkText : defaults.linkText,
      buttonColor: typeof parsed.buttonColor === 'string' ? parsed.buttonColor : defaults.buttonColor,
      textColor: typeof parsed.textColor === 'string' ? parsed.textColor : defaults.textColor,
      buttonSize: VALID_BUTTON_SIZES.includes(parsed.buttonSize as ButtonSizeOption)
        ? (parsed.buttonSize as ButtonSizeOption)
        : defaults.buttonSize,
      positioning: VALID_POSITIONING.includes(parsed.positioning as PositioningOption)
        ? (parsed.positioning as PositioningOption)
        : defaults.positioning,
    }
  } catch {
    return defaults
  }
}

function saveSettingsToStorage(settings: StoredEmbedSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
  } catch {
    // ignore quota / private mode
  }
}

interface AddToWebsiteModalProps {
  onClose: () => void
  bookingPageUrl: string
  initialLabel?: string
  initialButtonColor?: string
  initialTextColor?: string
  initialButtonSize?: ButtonSizeOption
  initialPositioning?: PositioningOption
}

const BUTTON_SIZE_OPTIONS: { value: ButtonSizeOption; label: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'large', label: 'Large' },
  { value: 'larger', label: 'Larger' },
]

const POSITIONING_OPTIONS: { value: PositioningOption; label: string }[] = [
  { value: 'neutral', label: 'Neutral' },
  { value: 'centered', label: 'Centered' },
]

export const AddToWebsiteModal: FC<AddToWebsiteModalProps> = ({
  onClose,
  bookingPageUrl,
  initialLabel = EMBED_DEFAULT_LABEL,
  initialButtonColor = EMBED_DEFAULT_BUTTON_COLOR,
  initialTextColor = EMBED_DEFAULT_TEXT_COLOR,
  initialButtonSize = EMBED_DEFAULT_BUTTON_SIZE,
  initialPositioning = EMBED_DEFAULT_POSITIONING,
}) => {
  const [settings, setSettings] = useState<StoredEmbedSettings>(() =>
    loadSettingsFromStorage({
      onClose,
      bookingPageUrl,
      initialLabel,
      initialButtonColor,
      initialTextColor,
      initialButtonSize,
      initialPositioning,
    }),
  )
  const [justCopied, setJustCopied] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  const { linkText, buttonColor, textColor, buttonSize, positioning } = settings

  const embedSnippet = buildEmbedSnippet(window.location.origin, bookingPageUrl, {
    label: linkText,
    buttonColor,
    textColor,
    buttonSize,
    positioning,
  })

  const persistSettings = () => {
    saveSettingsToStorage(settings)
  }

  const handleCopyCode = () => {
    persistSettings()
    void navigator.clipboard.writeText(embedSnippet).then(() => {
      setJustCopied(true)
      setTimeout(() => setJustCopied(false), 1500)
    })
  }

  const handleCodeAreaClick = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    persistSettings()
    ;(e.target as HTMLTextAreaElement).select()
  }

  return (
    <Modal
      onClose={onClose}
      title={
        <Typography variant="h3" className={classes.title}>
          Add a booking button to your website
        </Typography>
      }
      isCentered
      className={classes.modal}
      contentClassName={classes.content}
      style={{ maxWidth: 920, width: '92vw' }}
    >
      <div className={`${classes.column} ${classes.leftColumn}`}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Settings
        </Typography>
        <Typography variant="body" className={classes.sectionSubtitle}>
          Customize how your booking button appears on your website.
        </Typography>
        <div className={classes.settingsFields}>
          <TextField
            label="Link text"
            value={linkText}
            onChange={(e) => setSettings((s) => ({ ...s, linkText: e.target.value }))}
            type="text"
          />
          <FormControl label="Background color of the button">
            <div className={classes.colorInputWrapper}>
              <input
                type="color"
                className={classes.colorInput}
                value={buttonColor}
                onChange={(e) => setSettings((s) => ({ ...s, buttonColor: e.target.value }))}
                aria-label="Button color"
              />
            </div>
          </FormControl>
          <FormControl label="Color of the button label">
            <div className={classes.colorInputWrapper}>
              <input
                type="color"
                className={classes.colorInput}
                value={textColor}
                onChange={(e) => setSettings((s) => ({ ...s, textColor: e.target.value }))}
                aria-label="Text color"
              />
            </div>
          </FormControl>
          <button
            type="button"
            className={classes.advancedToggle}
            onClick={() => setShowAdvanced((v) => !v)}
            aria-expanded={showAdvanced}
          >
            {showAdvanced ? 'Hide advanced settings' : 'Show advanced settings'}
          </button>
          {showAdvanced && (
            <>
              <FormControl label="Button size">
                <div className={classes.radioGroup} role="radiogroup" aria-label="Button size">
                  {BUTTON_SIZE_OPTIONS.map((opt) => (
                    <label key={opt.value} className={classes.radioOption}>
                      <input
                        type="radio"
                        name="bwm-size"
                        value={opt.value}
                        checked={buttonSize === opt.value}
                        onChange={() => setSettings((s) => ({ ...s, buttonSize: opt.value }))}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </FormControl>
              <FormControl label="Positioning">
                <div className={classes.radioGroup} role="radiogroup" aria-label="Positioning">
                  {POSITIONING_OPTIONS.map((opt) => (
                    <label key={opt.value} className={classes.radioOption}>
                      <input
                        type="radio"
                        name="bwm-position"
                        value={opt.value}
                        checked={positioning === opt.value}
                        onChange={() => setSettings((s) => ({ ...s, positioning: opt.value }))}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </FormControl>
            </>
          )}
        </div>
      </div>

      <div className={`${classes.column} ${classes.rightColumn}`}>
        <Typography variant="h5" className={classes.sectionTitle}>
          Embed code
        </Typography>
        <Typography variant="body" className={classes.sectionSubtitle}>
          Copy and paste this code into your website where you want the button to appear. When clicked, your booking
          page will open in a new tab.
        </Typography>
        <TextField
          Element="textarea"
          readOnly
          value={embedSnippet}
          className={classes.codeTextarea}
          rows={11}
          onClick={handleCodeAreaClick}
        />
        <Typography variant="body" className={classes.embedHelpText}>
          Need help? See guides for embedding on{' '}
          <a href="https://www.smsreminder.app/integrations/shopify" target="_blank" rel="noopener noreferrer">
            Shopify
          </a>{' '}
          and{' '}
          <a href="https://www.smsreminder.app/integrations/wordpress" target="_blank" rel="noopener noreferrer">
            WordPress
          </a>
          , or explore our{' '}
          <a href="https://www.smsreminder.app/integrations" target="_blank" rel="noopener noreferrer">
            integrations
          </a>
          .
        </Typography>
        <div className={classes.buttonRow}>
          <button type="button" className={classes.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <Button
            variant="primary"
            onClick={handleCopyCode}
            className={justCopied ? classes.copyButtonFlash : undefined}
          >
            {justCopied ? 'Copied!' : 'Copy Code'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
